import React, {
  useState,
  forwardRef,
  useImperativeHandle,
  useRef,
} from 'react';
import {
  IDoesFilterPassParams,
  IFilterParams,
  IFilterComp,
} from 'ag-grid-community';
import DateFilterComponent, {
  DateFilterValue,
  FilterType,
} from './DateFilter/DateFilterComponent';

// Extended IFilterParams to include custom filterParams properties
// Note: ag-Grid passes filterParams properties directly on the props object
interface ICustomFilterParams extends IFilterParams {
  defaultOption?: FilterType;
}

// ag-Grid DateFilterModel interface
interface DateFilterModel {
  filterType: 'date';
  type: FilterType;
  dateFrom: string;
  dateTo?: string;
}

// Type definitions for ag-Grid internal APIs (not in official type definitions)
interface IMenuFactory {
  hideActiveMenu?: () => void;
}

interface IPopupService {
  hidePopup?: () => void;
}

interface IContext {
  getBean?: (name: string) => IPopupService | undefined;
}

// Type-safe access to ag-Grid internal APIs
interface IGridApiInternals {
  menuFactory?: IMenuFactory;
  context?: IContext;
}

// Helper type to access private properties safely
type GridApiWithInternals = Partial<IGridApiInternals> &
  Record<string, unknown>;

// Type assertion helper to access ag-Grid internal APIs
const getGridApiInternals = (api: unknown) => api as GridApiWithInternals;

// Helper to combine date and time into a single Date object
const getCombinedDateTime = (date?: Date, timeStr?: string): Date => {
  if (!date || !timeStr) {
    return new Date();
  }
  const [hours, minutes] = timeStr.split(':').map(Number);
  const combined = new Date(date);
  combined.setHours(hours ?? 0, minutes ?? 0, 0, 0);
  return combined;
};

// Helper to extract time string from Date
const extractTimeString = (date: Date): string => {
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  return `${hours}:${minutes}`;
};

const AgGridDateFilter = forwardRef<IFilterComp, ICustomFilterParams>(
  (props, ref) => {
    const [filterValue, setFilterValue] = useState<DateFilterValue | null>(
      null
    );
    const guiRef = useRef<HTMLDivElement>(null);

    useImperativeHandle(ref, () => ({
      // Required: Returns the DOM element for ag-Grid to render
      getGui(): any {
        return guiRef.current;
      },

      // Required: Tells ag-Grid if filter is currently active
      isFilterActive(): boolean {
        return filterValue != null;
      },

      // Required: THE CORE FILTERING LOGIC - filters each row
      // NOTE: Backend handles all filtering - we don't filter on the frontend
      // This method is required by ag-Grid interface but always returns true
      doesFilterPass(params: IDoesFilterPassParams): boolean {
        // Always return true - backend already filtered the data
        return true;
      },

      // Required: Returns current filter state (for persistence)
      getModel(): DateFilterModel | null {
        if (!filterValue) {
          return null;
        }

        const model: DateFilterModel = {
          filterType: 'date',
          type: filterValue.type,
          dateFrom: getCombinedDateTime(
            filterValue.startDate,
            filterValue.startTime
          )?.toISOString(),
        };

        const isInRangeFilterWithEndDate =
          filterValue.type === 'inRange' &&
          filterValue.endDate &&
          filterValue.endTime;

        if (isInRangeFilterWithEndDate) {
          model.dateTo = getCombinedDateTime(
            filterValue.endDate,
            filterValue.endTime
          ).toISOString();
        }

        return model;
      },

      // Required: Restores filter state (from persistence)
      setModel(model: DateFilterModel | null): void {
        if (!model || !model.dateFrom) {
          setFilterValue(null);
          return;
        }

        const dateFrom = new Date(model.dateFrom);
        const value: DateFilterValue = {
          type: model.type,
          startDate: dateFrom,
          startTime: extractTimeString(dateFrom),
        };

        const isInRangeFilterWithEndDate = model.type === 'inRange';

        if (isInRangeFilterWithEndDate && model.dateTo) {
          const dateTo = new Date(model.dateTo);
          value.endDate = dateTo;
          value.endTime = extractTimeString(dateTo);
        }

        setFilterValue(value);
      },
    }));

    const handleApply = (value: DateFilterValue | null) => {
      setFilterValue(value);
      props.filterChangedCallback?.();
    };

    const handleReset = () => {
      setFilterValue(null);
      props.filterChangedCallback?.();
    };

    const handleClose = () => {
      props.filterModifiedCallback?.();

      if (!props.api) {
        return;
      }

      const gridApi = getGridApiInternals(props.api);

      // Try menuFactory.hideActiveMenu()
      if (gridApi.menuFactory?.hideActiveMenu) {
        gridApi.menuFactory.hideActiveMenu();
        return;
      }

      // Try accessing popupService through context
      if (gridApi.context?.getBean) {
        try {
          const popupService = gridApi.context.getBean('popupService');
          if (popupService?.hidePopup) {
            popupService.hidePopup();
            return;
          }
        } catch (e) {
          // Ignore - popupService might not be available
        }
      }

      // Fallback: Simulate ESC key press
      document.dispatchEvent(
        new KeyboardEvent('keydown', {
          key: 'Escape',
          code: 'Escape',
          keyCode: 27,
          which: 27,
          bubbles: true,
          cancelable: true,
        })
      );
    };

    // Get default filter type from props (ag-Grid passes filterParams properties directly)
    const defaultFilterType =
      (props.defaultOption as FilterType) || 'greaterThan';

    return (
      <div ref={guiRef} className="ag-custom-filter-container">
        <DateFilterComponent
          onApply={handleApply}
          onReset={handleReset}
          onClose={handleClose}
          currentFilter={filterValue}
          defaultFilterType={defaultFilterType}
        />
      </div>
    );
  }
);

AgGridDateFilter.displayName = 'AgGridDateFilter';

export default AgGridDateFilter;
